// frontend/src/Components/SyncTo/FailureModal.jsx
//
// Phase 34 — Failure fallback. Replaces ReviewModal when /transcribe exhausts
// its 3-attempt backoff. Gives the user three exits:
//
//   1. Download audio — produces `session-<short8>-<ISO>.webm` from the
//      assembled Blob. IDB chunks remain until Dismiss so this download can
//      be repeated (or Retry can run).
//   2. Retry         — re-runs /transcribe with the SAME assembled Blob.
//      On success the parent switches to <ReviewModal>; on second exhausted
//      failure, the same FailureModal stays open with the attempts count
//      bumped.
//   3. Dismiss       — purges IDB chunks for this session (SYNC-06-like)
//      and closes the modal. Audio is NOT recoverable after Dismiss.
//
// A separate `timeoutMode` render path (prop-driven) renders the "Still
// processing — wait?" dialog (PITFALLS #7). Timeout mode shows Wait /
// Download / Dismiss — no auto-retry. Wait closes the dialog and the user
// can click SyncTo again to re-submit (or they can choose Retry here to
// re-fire /transcribe at their discretion).
//
// Invariants:
//   - Does NOT manage show internally; parent controls open/close.
//   - No PHI logging — zero console.* calls.
//   - No dangerouslySetInnerHTML.
//   - Retry disables itself while in-flight (prevents double-submit).

import React, { useState } from 'react';
import { Modal, Button, Alert, Spinner } from 'react-bootstrap';
import { assembleBlob, purgeBySessionKey } from './hooks/useSyncToIDB';
import { downloadBlob, buildDownloadFilename } from './helpers/downloadBlob';

export default function FailureModal({
  show,
  onClose,
  sessionKey,
  mimeType,
  attempts,
  errorMessage,
  timeoutMode,       // boolean — true → render "Still processing — wait?" variant
  onRetry,           // async (blob) => void ; parent calls transcribe again with the same Blob
  onDismiss,         // () => void ; parent clears failure state
}) {
  const [retrying, setRetrying] = useState(false);
  const [downloadError, setDownloadError] = useState(null);

  const onDownloadClick = async () => {
    if (!sessionKey) return;
    setDownloadError(null);
    try {
      const blob = await assembleBlob(sessionKey, mimeType);
      if (!blob || blob.size === 0) {
        setDownloadError('No audio found for this session — unable to download.');
        return;
      }
      downloadBlob(blob, buildDownloadFilename(sessionKey, mimeType));
    } catch (err) {
      setDownloadError((err && err.message) || 'Download failed');
    }
  };

  const onRetryClick = async () => {
    if (!sessionKey || typeof onRetry !== 'function') return;
    setRetrying(true);
    try {
      const blob = await assembleBlob(sessionKey, mimeType);
      if (!blob || blob.size === 0) {
        setDownloadError('No audio to retry — please dismiss and record again.');
        setRetrying(false);
        return;
      }
      await onRetry(blob);
    } finally {
      setRetrying(false);
    }
  };

  const onDismissClick = async () => {
    if (sessionKey) {
      try { await purgeBySessionKey(sessionKey); } catch { /* swallow */ }
    }
    if (typeof onDismiss === 'function') onDismiss();
  };

  const title = timeoutMode
    ? 'Still processing — wait?'
    : 'Transcription failed after ' + (attempts || 3) + ' retries';

  const body = timeoutMode ? (
    <p className="mb-0">
      Azure is still working on this transcription. You can wait and try again
      later, or download the audio now to keep it safe. Retry will fire a fresh
      transcription request.
    </p>
  ) : (
    <>
      <p className="mb-2">
        Azure transcription failed after {attempts || 3} retries.
      </p>
      {errorMessage && (
        <Alert variant="warning" className="mb-2 py-2 small">
          {errorMessage}
        </Alert>
      )}
      <p className="small text-muted mb-0">
        Your audio is still here. Download it for manual handling, or Retry
        to fire a fresh transcription request.
      </p>
    </>
  );

  return (
    <Modal
      show={show}
      onHide={onClose}
      size="lg"
      centered
      dialogClassName="responsive-modal"
      backdrop="static"
      keyboard={false}
    >
      <Modal.Header
        closeButton
        style={{ background: "#CFE2FF" }}
        className="rounded-0 text-primary d-flex align-items-center"
      >
        <Modal.Title as="h6" className="m-0">{title}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {downloadError && (
          <Alert variant="danger" className="mb-3">
            {downloadError}
          </Alert>
        )}
        {body}
      </Modal.Body>
      <Modal.Footer>
        <div className="d-flex justify-content-between w-100">
          <Button variant="outline-danger" onClick={onDismissClick} disabled={retrying}>
            Dismiss (delete audio)
          </Button>
          <div className="d-flex gap-2">
            <Button variant="outline-secondary" onClick={onDownloadClick} disabled={retrying}>
              <i className="fa-light fa-download me-2" />
              Download audio
            </Button>
            <Button variant="primary" onClick={onRetryClick} disabled={retrying || !sessionKey}>
              {retrying ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  Retrying&hellip;
                </>
              ) : (
                'Retry'
              )}
            </Button>
          </div>
        </div>
      </Modal.Footer>
    </Modal>
  );
}
